/**
 * What a card says when its read came back with nothing — a drawing, a sentence, and sometimes a
 * line about where the missing thing is made.
 *
 * ── Three kinds of empty, and they are not interchangeable ────────────────────────────────────
 * Owner, 2 September 2026, and this is the distinction the whole component exists to keep:
 *
 * | tone | what it means | example |
 * |---|---|---|
 * | `positive` | the trainer has dealt with everything. **This is not an absence.** | "Nic nie czeka na Twoją reakcję" |
 * | `action` | something is missing and there is somewhere to go and make it | "Ten klient nie ma jeszcze pomiarów" |
 * | `neutral` | a window with nothing in it. It informs and suggests nothing | "Brak danych w tym zakresie" |
 *
 * The tone is what picks the ink: `--good-ink` for the completion mark, `--text-2` for a thing the
 * trainer can act on, `--text-3` for a statement of fact. It is a SHAPE in that green and never a
 * glow — the coloured-blur ban is absolute and a "positive" state is exactly where a designer
 * reaches for one.
 *
 * `--good-ink` rather than `--good`, and the difference matters only in the light themes: it is the
 * same green moved per theme, which is what this app already does for `--lime-ink`, and in Flame
 * Dark the two tokens are literally the same value. No KANON specimen draws these states, so there
 * is no canon value to depart from — see the round's report for the measured contrast of both.
 *
 * ── It never owns the height ──────────────────────────────────────────────────────────────────
 * The card around it does, through its own reserve. This block centres itself in whatever it is
 * given and adds nothing to it, because the whole point of a reserve is that the short state and
 * the loaded state stand in one box. A caller that grew when it started drawing an icon would have
 * traded a copy defect for the layout defect the reserves exist to prevent.
 *
 * ── `inline` ──────────────────────────────────────────────────────────────────────────────────
 * One variant, for a sentence that lives INSIDE a drawn card rather than instead of one — the goal
 * card's "Brak pomiaru…" line under a ring that has no arc. There the drawing sits on the sentence's
 * own line at text size, so the card it is part of does not grow by a row.
 */

import type { ReactNode } from 'react';
import { motion } from 'motion/react';

import type { ScreenMotionStep } from '~/shared/design-system/motion';

import styles from './EmptyState.module.css';

export type EmptyStateTone = 'positive' | 'action' | 'neutral';

interface EmptyStateProps {
  /** The drawing, from `EmptyStateIcons`. Passed as a node because the caller knows which concept is missing. */
  readonly mark: ReactNode;
  readonly tone: EmptyStateTone;
  /**
   * The sentence. Always the locale's own words — copy is never written here.
   *
   * A node rather than a string because one of these sentences carries a control inside it: the
   * goal card's "Brak pomiaru — …" ends in the tab link that is the act. Every other call site
   * passes a plain `t(...)`.
   */
  readonly title: ReactNode;
  /** The second line, where one exists: what the trainer can do, or where the thing is made. */
  readonly hint?: string;
  /** A sentence inside a card rather than instead of one. See the header. */
  readonly inline?: boolean;
  /**
   * A state that stands in for a whole SCREEN rather than a card: the sentence becomes the
   * screen's heading, the hint reads as plain text under it, and the mark sits on a round chip.
   * Used where nothing else is drawn around it (the empty Zadania queue, owner's E2, 5 November
   * 2026). Ignored by the inline variant.
   */
  readonly page?: boolean;
  /**
   * The type the INLINE variant is set in — its size, weight, ink and style.
   *
   * The inline variant declares layout only, because a sentence inside a drawn card is set in that
   * card's own voice: the note's empty line is italic `--fs-note`, the goal's is `--fs-micro`, and
   * a component that picked one of them would be wrong on the other surface. It reaches the ROOT
   * element and nothing else — the mark, the tone and the arrangement stay this component's.
   *
   * The BLOCK variant takes it for one reason only: to be told how much of its parent to occupy.
   * `.empty` already centres its contents; a caller whose card gives the state the whole remaining
   * column says so with `flex: 1` and nothing else. Type still belongs to this component there.
   */
  readonly className?: string;
  /**
   * How this state ARRIVES, for the one surface where reaching it is something the viewer just
   * did: the trainer's home draws its mark and then raises its sentence under it when the last
   * task of the morning is closed. Both parts are given together because a mark that appeared
   * while its sentence stood still would read as two states rather than one.
   *
   * Omitted — every other caller — the state is simply there, which is what a card whose read came
   * back empty looks like. The BLOCK variant honours it; `inline` is a sentence inside someone
   * else's card and arrives with that card.
   */
  readonly entrance?: EmptyStateEntrance;
}

export interface EmptyStateEntrance {
  /** The drawing, which appears whole. */
  readonly mark: ScreenMotionStep;
  /** The sentence and its hint, which rise under it. */
  readonly text: ScreenMotionStep;
}

const TONE_CLASS: Readonly<Record<EmptyStateTone, string>> = {
  positive: String(styles.tonePositive),
  action: String(styles.toneAction),
  neutral: String(styles.toneNeutral),
};

/**
 * The two optional props take their defaults in the BODY, not in the parameter list: a destructured
 * default makes `babel-plugin-react-compiler@1.0.0` skip the whole component without saying so —
 * `@babel/types` 8 removed `AssignmentPattern` from the `LVal` alias the plugin gates on. See
 * `shared/ui/chart/ReadoutChart`, which carries the measurement.
 */
export function EmptyState({
  mark,
  tone,
  title,
  hint,
  inline: inlineProp,
  page: pageProp,
  className: classNameProp,
  entrance,
}: EmptyStateProps) {
  const inline = inlineProp ?? false;
  const page = pageProp ?? false;
  const className = classNameProp ?? '';
  if (inline) {
    return (
      <p className={`${String(styles.inline)} ${className}`}>
        <span className={`${styles.inlineMark} ${TONE_CLASS[tone]}`}>{mark}</span>
        {title}
      </p>
    );
  }

  return (
    <div className={`${String(styles.empty)} ${page ? String(styles.page) : ''} ${className}`}>
      <motion.span className={`${styles.mark} ${TONE_CLASS[tone]}`} {...entrance?.mark}>
        {mark}
      </motion.span>
      <motion.p className={styles.title} {...entrance?.text}>
        {title}
      </motion.p>
      {hint === undefined ? null : (
        <motion.p className={styles.hint} {...entrance?.text}>
          {hint}
        </motion.p>
      )}
    </div>
  );
}
